import {Component, OnDestroy, OnInit} from "@angular/core";
import {MatTableDataSource} from "@angular/material/table";
import {MatDialog} from "@angular/material/dialog";
import {Subject} from "rxjs";
import {debounceTime, distinctUntilChanged, takeUntil} from "rxjs/operators";
import {AdminControllerService} from "../../../core/api/controllers/admin/admin-controller.service";
import {
  EBillingPeriod,
  IBillingHealthCustomer,
  IBillingHealthSummary
} from "../../../core/api/controllers/admin/data/IBillingHealth";
import {GlobalPreferences} from "../../../core/helpers/global-data";
import {NotificationService} from "../../../core/services/notification.service";
import {EBillingHealthStatus} from "../../../shared/enums/EBillingHealthStatus";
import {EAttemptPaymentStatus} from "../../../shared/enums/EAttemptPaymentStatus";
import {
  ConfirmationDialogComponent,
  IConfirmationDialogData
} from "../../../shared/components/confirmation-dialog/confirmation-dialog.component";

@Component({
  selector: "app-billing-health",
  templateUrl: "./billing-health.component.html",
  styleUrls: ["./billing-health.component.scss"],
})
export class BillingHealthComponent implements OnInit, OnDestroy {
  readonly displayedColumns = [
    "customer",
    "billingStatus",
    "subscription",
    "nextBillingDate",
    "lastSuccessfulCharge",
    "latestAttempt",
    "paymentMethod",
    "actions",
  ];
  readonly billingHealthStatus = EBillingHealthStatus;
  readonly maxMonth = this.toMonthInput(new Date());

  isLoading = false;
  retryingSubscriptionId: string | null = null;
  selectedMonth = this.maxMonth;
  selectedStatus: EBillingHealthStatus | null = null;
  search = "";
  pageIndex = 0;
  pageSize = 10;
  totalCount = 0;
  dataSource = new MatTableDataSource<IBillingHealthCustomer>([]);
  summary: IBillingHealthSummary = {
    successfullyBilled: 0,
    failed: 0,
    overdue: 0,
    dueLater: 0,
    excluded: 0,
  };

  private readonly searchChanged$ = new Subject<string>();
  private readonly destroyed$ = new Subject<void>();

  constructor(
    private readonly adminController: AdminControllerService,
    private readonly global: GlobalPreferences,
    private readonly notificationService: NotificationService,
    private readonly dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.searchChanged$
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        takeUntil(this.destroyed$)
      )
      .subscribe(() => {
        this.pageIndex = 0;
        this.fetchBillingHealth();
      });

    this.fetchBillingHealth();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onSearchChanged(value: string): void {
    this.searchChanged$.next(value);
  }

  onFiltersChanged(): void {
    this.pageIndex = 0;
    this.fetchBillingHealth();
  }

  selectStatus(status: EBillingHealthStatus): void {
    this.selectedStatus = this.selectedStatus === status ? null : status;
    this.onFiltersChanged();
  }

  clearFilters(): void {
    this.search = "";
    this.selectedStatus = null;
    this.selectedMonth = this.maxMonth;
    this.onFiltersChanged();
  }

  pageChanged(event: {pageIndex: number; pageSize: number}): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.fetchBillingHealth();
  }

  fetchBillingHealth(): void {
    this.isLoading = true;
    this.adminController
      .getBillingHealth(
        {
          month: `${this.selectedMonth}-01`,
          search: this.search.trim(),
          status: this.selectedStatus,
          gmtOffset: this.global.getTimezoneOffset(),
        },
        this.pageIndex,
        this.pageSize
      )
      .subscribe({
        next: response => {
          this.summary = response.summary;
          this.totalCount = response.totalCount;
          this.dataSource.data = response.data;
          this.isLoading = false;
        },
        error: error => {
          this.notificationService.error(error);
          this.isLoading = false;
        },
      });
  }

  openRetryPaymentDialog(customer: IBillingHealthCustomer): void {
    const data: IConfirmationDialogData = {
      title: "Retry payment",
      message: `Retry the payment for “${customer.organizationName}”?`,
    };

    this.dialog
      .open(ConfirmationDialogComponent, {data})
      .afterClosed()
      .subscribe(confirmed => {
        if (confirmed) this.retryPayment(customer);
      });
  }

  retryPayment(customer: IBillingHealthCustomer): void {
    this.retryingSubscriptionId = customer.organizationSubscriptionId;
    this.adminController
      .triggerPayment(customer.organizationSubscriptionId)
      .subscribe({
        next: response => {
          if (response.status === EAttemptPaymentStatus.Paid) {
            this.notificationService.success("The payment was completed successfully.");
          } else if (response.status === EAttemptPaymentStatus.NotRequired) {
            this.notificationService.warn("This customer does not currently require a payment.");
          }
          this.retryingSubscriptionId = null;
          this.fetchBillingHealth();
        },
        error: () => {
          this.notificationService.error("The payment failed. The attempt is now recorded in billing history.");
          this.retryingSubscriptionId = null;
          this.fetchBillingHealth();
        },
      });
  }

  canRetry(customer: IBillingHealthCustomer): boolean {
    return !customer.paysOutsideApp &&
      customer.price > 0 &&
      (customer.billingHealthStatus === EBillingHealthStatus.Failed ||
        customer.billingHealthStatus === EBillingHealthStatus.Overdue);
  }

  getStatusLabel(status: EBillingHealthStatus): string {
    return {
      [EBillingHealthStatus.SuccessfullyBilled]: "Successfully billed",
      [EBillingHealthStatus.Failed]: "Failed",
      [EBillingHealthStatus.Overdue]: "Overdue — no attempt",
      [EBillingHealthStatus.DueLater]: "Due later",
      [EBillingHealthStatus.Excluded]: "Excluded",
    }[status];
  }

  getBillingPeriodLabel(period: EBillingPeriod): string {
    return period === EBillingPeriod.Annually ? "Annual" : "Monthly";
  }

  getFailureReason(reason: string | null): string {
    if (!reason) return "Payment declined";
    if (reason === "MissingPaymentMethod") return "Missing payment method";
    return reason.replace(/([a-z])([A-Z])/g, "$1 $2");
  }

  private toMonthInput(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
  }
}
